import { useQuery, useQueryClient } from "@tanstack/react-query";
// PageHeader replaces the icon + back-link composite.
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import { Badge } from "../components/Badge";
import { ErrorPanel, Loading } from "../components/Loading";
import { EditableField } from "../components/ui/EditableField";
import { PageHeader } from "../components/ui/PageHeader";
import { api } from "../lib/api";

export function ExcuseTypeDetailPage() {
  const { t } = useTranslation();
  const { excuseTypeId } = useParams<{ excuseTypeId: string }>();
  const qc = useQueryClient();

  const etQ = useQuery({
    queryKey: ["excuse-type", excuseTypeId],
    queryFn: () => api.getExcuseType(excuseTypeId!),
    enabled: !!excuseTypeId,
  });

  if (etQ.isLoading) return <Loading />;
  if (etQ.error) return <ErrorPanel message={(etQ.error as Error).message} />;
  if (!etQ.data) return <ErrorPanel message={t("policies.excuse_types_not_found")} />;

  const e = etQ.data;

  const patch = (patch: Record<string, unknown>) =>
    api.updateExcuseType(e.id, patch).then((updated) => {
      qc.setQueryData(["excuse-type", e.id], updated);
      qc.invalidateQueries({ queryKey: ["excuse-types"] });
      return updated;
    });

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={`${e.code} · ${e.name_en}`}
        breadcrumbs={[
          { label: t("policies.excuse_types_back"), href: "/policies/excuse-types" },
          { label: e.code },
        ]}
        actions={
          !e.is_active ? (
            <Badge tone="slate">{t("policies.loan_products_inactive_badge")}</Badge>
          ) : undefined
        }
      />

      <section className="rounded-xl border border-slate-200 bg-white p-6">
        <h2 className="mb-4 text-sm font-semibold uppercase tracking-wider text-slate-500">
          {t("policies.excuse_types_section_identity")}
        </h2>
        <div className="grid gap-4 md:grid-cols-2">
          <EditableField
            label={t("policies.name_en_label")}
            value={e.name_en}
            onSave={(v) => patch({ name_en: v })}
          />
          <EditableField
            label={t("policies.name_ar_label")}
            value={e.name_ar}
            rtl
            onSave={(v) => patch({ name_ar: v })}
          />
          <EditableField
            label={t("policies.description_label")}
            value={e.description}
            kind="textarea"
            onSave={(v) => patch({ description: v })}
          />
          <EditableField
            label={t("policies.allowances_category_label")}
            value={e.excuse_category}
            kind="select"
            options={[
              { value: "late", label: t("policies.excuse_types_cat_late") },
              { value: "early", label: t("policies.excuse_types_cat_early") },
              { value: "permission", label: t("policies.excuse_types_cat_permission") },
              { value: "sick", label: t("policies.excuse_types_cat_sick") },
              { value: "personal", label: t("policies.excuse_types_cat_personal") },
            ]}
            onSave={(v) => patch({ excuse_category: v })}
          />
          <EditableField
            label={t("policies.active_label")}
            value={e.is_active}
            kind="boolean"
            onSave={(v) => patch({ is_active: v })}
          />
        </div>
      </section>

      <section className="rounded-xl border border-slate-200 bg-white p-6">
        <h2 className="mb-4 text-sm font-semibold uppercase tracking-wider text-slate-500">
          {t("policies.excuse_types_section_limits")}
        </h2>
        <div className="grid gap-4 md:grid-cols-2">
          <EditableField
            label={t("policies.excuse_types_max_per_month")}
            value={e.max_per_month}
            kind="number"
            onSave={(v) =>
              patch({ max_per_month: v == null ? null : Number(v) })
            }
          />
          <EditableField
            label={t("policies.excuse_types_max_per_year")}
            value={e.max_per_year}
            kind="number"
            onSave={(v) =>
              patch({ max_per_year: v == null ? null : Number(v) })
            }
          />
          <EditableField
            label={t("policies.excuse_types_requires_attachment")}
            value={e.requires_attachment}
            kind="boolean"
            onSave={(v) => patch({ requires_attachment: v })}
          />
          <EditableField
            label={t("policies.excuse_types_paid")}
            value={e.paid}
            kind="boolean"
            onSave={(v) => patch({ paid: v })}
          />
          <EditableField
            label={t("policies.excuse_types_zero_minutes")}
            value={e.affects_attendance}
            kind="boolean"
            onSave={(v) => patch({ affects_attendance: v })}
          />
        </div>
      </section>
    </div>
  );
}
